/**
 * SoleadLab 2.0 — IV Measurements Table
 */

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useIVStore } from '@/stores/ivStore';
import type { IVMeasurement, UpdateMeasurementInput } from '@/types/iv';

// ─── Extras columns that can be shown/hidden ──────────────────────────────────

interface ExtrasColumnDef {
  key: string;
  label: string;
}

const EXTRAS_COLUMNS: ExtrasColumnDef[] = [
  { key: 'test_number',      label: 'Test #' },
  { key: 'timestamp',        label: 'Timestamp' },
  { key: 'pmax_nominal',     label: 'Pmax Nom.' },
  { key: 'degradation_pct',  label: 'Degradation %' },
  { key: 'pmax_opc',         label: 'Pmax OPC' },
  { key: 'irr_back',         label: 'Irr. Back' },
  { key: 'ref_cell_temp',    label: 'Ref. Temp' },
  { key: 'module_age_years', label: 'Age (y)' },
];

// ─── Sort / Filter types ──────────────────────────────────────────────────────

type FilterValue =
  | { type: 'values'; selected: Set<string> }
  | { type: 'range'; min: string; max: string };

const NUMERIC_COLS = new Set([
  'voltage_oc', 'current_sc', 'voltage_mpp', 'current_mpp',
  'power_mpp', 'fill_factor', 'irradiance', 'module_temp',
  // numeric extras
  'pmax_nominal', 'pmax_opc', 'irr_back', 'ref_cell_temp', 'module_age_years', 'degradation_pct',
]);

function colTypeOf(key: string): 'numeric' | 'string' {
  return NUMERIC_COLS.has(key) ? 'numeric' : 'string';
}

function getColValue(m: IVMeasurement, col: string): string | number | null {
  switch (col) {
    case 'included':    return m.included === 1 ? 'Included' : 'Excluded';
    case 'flagged':     return m.flagged === 1 ? 'Flagged' : 'Not flagged';
    case 'label':       return m.label ?? '';
    case 'marker2':     return m.marker2 ?? '';
    case 'marker3':     return m.marker3 ?? '';
    case 'result':      return m.result ?? '';
    case 'notes':       return m.notes ?? '';
    case 'voltage_oc':  return m.voltage_oc ?? null;
    case 'current_sc':  return m.current_sc ?? null;
    case 'voltage_mpp': return m.voltage_mpp ?? null;
    case 'current_mpp': return m.current_mpp ?? null;
    case 'power_mpp':   return m.power_mpp ?? null;
    case 'fill_factor': return m.fill_factor ?? null;
    case 'irradiance':  return m.irradiance ?? null;
    case 'module_temp': return m.module_temp ?? null;
    default: {
      // extras columns (test_number, timestamp, pmax_nominal, etc.)
      const v = m.extras?.[col];
      return v != null ? v : null;
    }
  }
}

function compareVals(a: string | number | null, b: string | number | null): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;   // nulls sort last
  if (b == null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b));
}

// ─── Numeric Range Filter Popover ─────────────────────────────────────────────

function NumericFilterPopover({
  colKey, filterState, onApply,
}: {
  colKey: string;
  filterState: FilterValue | undefined;
  onApply: (col: string, f: FilterValue | null) => void;
}) {
  const current = filterState?.type === 'range' ? filterState : undefined;
  const [min, setMin] = useState(current?.min ?? '');
  const [max, setMax] = useState(current?.max ?? '');

  const apply = () => {
    if (min === '' && max === '') onApply(colKey, null);
    else onApply(colKey, { type: 'range', min, max });
  };

  return (
    <div className="iv-filter-popover" style={{ position: 'absolute', top: '100%', left: 0, zIndex: 100 }}>
      <div className="iv-filter-popover__title">Range</div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 6, alignItems: 'center' }}>
        <input
          className="iv-input" placeholder="Min" value={min}
          onChange={(e) => setMin(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && apply()}
          style={{ width: 62 }} autoFocus
        />
        <span style={{ color: 'var(--text-muted)', fontSize: 'var(--sl-text-xs)' }}>–</span>
        <input
          className="iv-input" placeholder="Max" value={max}
          onChange={(e) => setMax(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && apply()}
          style={{ width: 62 }}
        />
      </div>
      <div style={{ display: 'flex', gap: 4 }}>
        <button className="iv-btn iv-btn--primary iv-btn--sm" onClick={apply}>Apply</button>
        <button className="iv-btn iv-btn--sm" onClick={() => onApply(colKey, null)}>Clear</button>
      </div>
    </div>
  );
}

// ─── Values (string/boolean) Filter Popover ───────────────────────────────────

function ValuesFilterPopover({
  colKey, measurements, filterState, onApply,
}: {
  colKey: string;
  measurements: IVMeasurement[];
  filterState: FilterValue | undefined;
  onApply: (col: string, f: FilterValue | null) => void;
}) {
  const uniqueVals = useMemo(
    () => Array.from(new Set(measurements.map((m) => String(getColValue(m, colKey) ?? '')))).sort(),
    [measurements, colKey],
  );
  const [selected, setSelected] = useState<Set<string>>(
    () => filterState?.type === 'values' ? new Set(filterState.selected) : new Set(uniqueVals),
  );

  const toggle = (val: string) =>
    setSelected((prev) => { const s = new Set(prev); if (s.has(val)) s.delete(val); else s.add(val); return s; });

  const apply = () => {
    if (selected.size === 0 || selected.size >= uniqueVals.length) onApply(colKey, null);
    else onApply(colKey, { type: 'values', selected: new Set(selected) });
  };

  return (
    <div className="iv-filter-popover" style={{ position: 'absolute', top: '100%', left: 0, zIndex: 100 }}>
      <div className="iv-filter-popover__header">
        <button className="iv-btn iv-btn--sm" onClick={() => setSelected(new Set(uniqueVals))}>All</button>
        <button className="iv-btn iv-btn--sm" onClick={() => setSelected(new Set())}>None</button>
      </div>
      <div className="iv-filter-popover__values">
        {uniqueVals.map((val) => (
          <label key={val} className="iv-filter-option">
            <input type="checkbox" checked={selected.has(val)} onChange={() => toggle(val)} />
            <span>{val || <em style={{ opacity: 0.5 }}>(empty)</em>}</span>
          </label>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
        <button className="iv-btn iv-btn--primary iv-btn--sm" onClick={apply}>Apply</button>
        <button className="iv-btn iv-btn--sm" onClick={() => onApply(colKey, null)}>Clear</button>
      </div>
    </div>
  );
}

// ─── Column Header (sortable + filterable) ────────────────────────────────────

function ColHeader({
  colKey, label, title,
  sortState, onSortToggle,
  filterState, onFilterChange,
  measurements,
}: {
  colKey: string;
  label: string;
  title?: string;
  sortState: { col: string; dir: 'asc' | 'desc' } | null;
  onSortToggle: (col: string) => void;
  filterState: FilterValue | undefined;
  onFilterChange: (col: string, f: FilterValue | null) => void;
  measurements: IVMeasurement[];
}) {
  const [open, setOpen] = useState(false);
  const isSorted = sortState?.col === colKey;
  const isFiltered = !!filterState;
  const type = colTypeOf(colKey);

  const handleApply = (col: string, f: FilterValue | null) => {
    onFilterChange(col, f);
    setOpen(false);
  };

  return (
    <div className="iv-col-header">
      <span
        className="iv-col-header__label"
        title={title ? `${title} — click to sort` : 'Click to sort'}
        onClick={() => onSortToggle(colKey)}
      >
        {label}
        {isSorted && (
          <span className="iv-sort-arrow">{sortState!.dir === 'asc' ? ' ↑' : ' ↓'}</span>
        )}
      </span>
      <button
        className={`iv-filter-btn${isFiltered ? ' iv-filter-btn--active' : ''}`}
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        title={isFiltered ? 'Filter active — click to edit' : 'Filter'}
      >
        ▾
      </button>
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setOpen(false)} />
          {type === 'numeric' ? (
            <NumericFilterPopover colKey={colKey} filterState={filterState} onApply={handleApply} />
          ) : (
            <ValuesFilterPopover colKey={colKey} measurements={measurements} filterState={filterState} onApply={handleApply} />
          )}
        </>
      )}
    </div>
  );
}

// ─── Result Cell ──────────────────────────────────────────────────────────────

function ResultCell({ result, resultPct }: { result: string | null; resultPct: number | null }) {
  if (!result) return <span style={{ color: 'var(--text-muted)', fontSize: 'var(--sl-text-xs)' }}>—</span>;
  const lower = result.toLowerCase();
  let color = 'var(--text-muted)', bg = 'transparent';
  if (lower.startsWith('ok') && !lower.includes('not')) { color = 'var(--green)'; bg = 'rgba(34,197,94,0.08)'; }
  else if (lower.includes('not ok')) {
    const pct = resultPct ?? 0;
    color = pct < -5 ? 'var(--red, #ef4444)' : 'var(--orange)';
    bg = pct < -5 ? 'rgba(239,68,68,0.08)' : 'rgba(249,115,22,0.08)';
  }
  return (
    <span title={result} className="iv-result-badge" style={{ display: 'inline-block', padding: '1px 5px', borderRadius: 4, fontSize: 'var(--sl-text-xs)', fontFamily: 'var(--font-mono)', fontWeight: 600, color, background: bg, whiteSpace: 'nowrap' }}>
      {result}
    </span>
  );
}

// ─── Column Picker ────────────────────────────────────────────────────────────

function ColumnPicker({ visibleExtras, onToggle }: { visibleExtras: Set<string>; onToggle: (key: string) => void }) {
  const [open, setOpen] = useState(false);
  const hasAny = EXTRAS_COLUMNS.some((c) => visibleExtras.has(c.key));
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button className="iv-btn iv-btn--sm" style={{ gap: 4, fontWeight: hasAny ? 600 : undefined }} onClick={() => setOpen((o) => !o)} title="Toggle optional columns">
        ⚙ Columns {hasAny ? `(${visibleExtras.size})` : ''}
      </button>
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 10 }} onClick={() => setOpen(false)} />
          <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 20, background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 6, padding: '6px 0', minWidth: 180, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
            <div style={{ padding: '4px 12px', fontSize: 'var(--sl-text-xs)', color: 'var(--text-muted)', fontWeight: 600 }}>OPTIONAL COLUMNS</div>
            {EXTRAS_COLUMNS.map((col) => (
              <label key={col.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 12px', cursor: 'pointer', fontSize: 'var(--sl-text-sm)' }}>
                <input type="checkbox" checked={visibleExtras.has(col.key)} onChange={() => onToggle(col.key)} />
                {col.label}
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Bulk Action Bar ──────────────────────────────────────────────────────────

function BulkActionBar({
  count,
  onApply,
  onClear,
}: {
  count: number;
  onApply: (updates: { label?: string | null; marker2?: string | null; marker3?: string | null }) => void;
  onClear: () => void;
}) {
  const [m1, setM1] = useState('');
  const [m2, setM2] = useState('');
  const [m3, setM3] = useState('');

  const handleApply = () => {
    const updates: { label?: string | null; marker2?: string | null; marker3?: string | null } = {};
    if (m1.trim() !== '') updates.label   = m1.trim();
    if (m2.trim() !== '') updates.marker2 = m2.trim();
    if (m3.trim() !== '') updates.marker3 = m3.trim();
    if (Object.keys(updates).length === 0) return;
    onApply(updates);
    setM1(''); setM2(''); setM3('');
  };

  const handleClearField = (field: 'label' | 'marker2' | 'marker3') => {
    onApply({ [field]: null });
    if (field === 'label')   setM1('');
    if (field === 'marker2') setM2('');
    if (field === 'marker3') setM3('');
  };

  return (
    <div className="iv-bulk-bar">
      <span className="iv-bulk-bar__count">{count} selected</span>
      <button className="iv-btn iv-btn--sm" onClick={onClear} title="Clear selection">✕ Clear</button>
      <div className="iv-bulk-bar__sep" />
      <span className="iv-bulk-bar__label">Set:</span>
      <div className="iv-bulk-bar__field">
        <span className="iv-bulk-bar__field-label">M1</span>
        <input className="iv-input iv-bulk-bar__input" value={m1} onChange={(e) => setM1(e.target.value)} placeholder="Marker 1…" onKeyDown={(e) => e.key === 'Enter' && handleApply()} />
        <button className="iv-bulk-bar__clear-field" title="Clear Marker 1 for selected" onClick={() => handleClearField('label')}>×</button>
      </div>
      <div className="iv-bulk-bar__field">
        <span className="iv-bulk-bar__field-label">M2</span>
        <input className="iv-input iv-bulk-bar__input" value={m2} onChange={(e) => setM2(e.target.value)} placeholder="Marker 2…" onKeyDown={(e) => e.key === 'Enter' && handleApply()} />
        <button className="iv-bulk-bar__clear-field" title="Clear Marker 2 for selected" onClick={() => handleClearField('marker2')}>×</button>
      </div>
      <div className="iv-bulk-bar__field">
        <span className="iv-bulk-bar__field-label">M3</span>
        <input className="iv-input iv-bulk-bar__input" value={m3} onChange={(e) => setM3(e.target.value)} placeholder="Marker 3…" onKeyDown={(e) => e.key === 'Enter' && handleApply()} />
        <button className="iv-bulk-bar__clear-field" title="Clear Marker 3 for selected" onClick={() => handleClearField('marker3')}>×</button>
      </div>
      <button className="iv-btn iv-btn--primary iv-btn--sm" onClick={handleApply}>Apply</button>
    </div>
  );
}

// ─── Main Table ───────────────────────────────────────────────────────────────

export default function IVMeasurementsTable() {
  const measurements = useIVStore((s) => s.measurements);
  const selectedSessionId = useIVStore((s) => s.selectedSessionId);
  const toggleIncluded = useIVStore((s) => s.toggleMeasurementIncluded);
  const updateMeasurement = useIVStore((s) => s.updateMeasurement);
  const bulkUpdateMeasurements = useIVStore((s) => s.bulkUpdateMeasurements);
  const deleteMeasurement = useIVStore((s) => s.deleteMeasurement);

  const [visibleExtras, setVisibleExtras] = useState<Set<string>>(new Set());
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const lastClickedIndexRef = useRef<number | null>(null);
  const shiftHeldRef = useRef(false);
  const [applying, setApplying] = useState(false);

  // ── Sort & filter state ──────────────────────────────────────────────────
  const [sortState, setSortState] = useState<{ col: string; dir: 'asc' | 'desc' } | null>(null);
  const [filters, setFilters] = useState<Record<string, FilterValue>>({});

  // ── Derived: filter then sort ────────────────────────────────────────────
  const filteredMeasurements = useMemo(() => {
    let result = measurements;
    for (const [col, filter] of Object.entries(filters)) {
      if (filter.type === 'values') {
        result = result.filter((m) => filter.selected.has(String(getColValue(m, col) ?? '')));
      } else {
        const lo = filter.min !== '' ? Number(filter.min) : -Infinity;
        const hi = filter.max !== '' ? Number(filter.max) : Infinity;
        result = result.filter((m) => {
          const v = getColValue(m, col);
          if (v == null) return false;
          const n = Number(v);
          return !isNaN(n) && n >= lo && n <= hi;
        });
      }
    }
    return result;
  }, [measurements, filters]);

  const displayedMeasurements = useMemo(() => {
    if (!sortState) return filteredMeasurements;
    const { col, dir } = sortState;
    return [...filteredMeasurements].sort((a, b) => {
      const cmp = compareVals(getColValue(a, col), getColValue(b, col));
      return dir === 'asc' ? cmp : -cmp;
    });
  }, [filteredMeasurements, sortState]);

  const handleSortToggle = useCallback((col: string) => {
    setSortState((prev) => {
      if (!prev || prev.col !== col) return { col, dir: 'asc' };
      if (prev.dir === 'asc') return { col, dir: 'desc' };
      return null; // third click → clear sort
    });
  }, []);

  const handleFilterChange = useCallback((col: string, filter: FilterValue | null) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (filter == null) delete next[col];
      else next[col] = filter;
      return next;
    });
  }, []);

  const handleClearAll = useCallback(() => {
    setFilters({});
    setSortState(null);
  }, []);

  useEffect(() => {
    const dn = (e: KeyboardEvent) => { if (e.key === 'Shift') shiftHeldRef.current = true; };
    const up = (e: KeyboardEvent) => { if (e.key === 'Shift') shiftHeldRef.current = false; };
    window.addEventListener('keydown', dn);
    window.addEventListener('keyup', up);
    return () => { window.removeEventListener('keydown', dn); window.removeEventListener('keyup', up); };
  }, []);

  const toggleExtrasCol = useCallback((key: string) => {
    setVisibleExtras((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  // Shift-click ranges operate on displayedMeasurements (respects sort/filter)
  const handleRowSelect = useCallback((id: string, index: number) => {
    const shiftHeld = shiftHeldRef.current;
    const lastIndex = lastClickedIndexRef.current;
    lastClickedIndexRef.current = index;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (shiftHeld && lastIndex !== null) {
        const lo = Math.min(lastIndex, index);
        const hi = Math.max(lastIndex, index);
        for (let i = lo; i <= hi; i++) {
          if (displayedMeasurements[i]) next.add(displayedMeasurements[i].id);
        }
      } else {
        if (next.has(id)) next.delete(id); else next.add(id);
      }
      return next;
    });
  }, [displayedMeasurements]);

  const handleSelectAll = useCallback((checked: boolean) => {
    setSelectedIds(checked ? new Set(displayedMeasurements.map((m) => m.id)) : new Set());
    lastClickedIndexRef.current = null;
  }, [displayedMeasurements]);

  const handleBulkApply = useCallback(async (updates: { label?: string | null; marker2?: string | null; marker3?: string | null }) => {
    if (!selectedSessionId || selectedIds.size === 0) return;
    setApplying(true);
    try {
      await bulkUpdateMeasurements(selectedSessionId, [...selectedIds], updates);
      setSelectedIds(new Set());
    } finally {
      setApplying(false);
    }
  }, [selectedSessionId, selectedIds, bulkUpdateMeasurements]);

  const includedCount = measurements.filter((m) => m.included === 1).length;
  const excludedCount = measurements.length - includedCount;
  const isFiltered = displayedMeasurements.length < measurements.length;
  const activeCount = Object.keys(filters).length + (sortState ? 1 : 0);
  const allSelected = displayedMeasurements.length > 0 && displayedMeasurements.every((m) => selectedIds.has(m.id));
  const someSelected = displayedMeasurements.some((m) => selectedIds.has(m.id)) && !allSelected;

  const availableExtrasKeys = new Set<string>();
  for (const m of measurements) {
    if (m.extras) for (const k of Object.keys(m.extras)) availableExtrasKeys.add(k);
  }

  const activeExtrasCols = EXTRAS_COLUMNS.filter((c) => visibleExtras.has(c.key) && availableExtrasKeys.has(c.key));
  const isCompact = activeExtrasCols.length > 0;

  if (measurements.length === 0) {
    return <div className="iv-note iv-note--info">No measurements imported yet. Use the Import tab to upload data.</div>;
  }

  // Shared props for every ColHeader
  const ch = {
    sortState,
    onSortToggle: handleSortToggle,
    onFilterChange: handleFilterChange,
    measurements, // full list — always show all unique values in filter
  };

  return (
    <div>
      {/* Header bar */}
      <div className="iv-table__footer" style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ flex: 1 }}>
          <span style={{ color: 'var(--green)', fontWeight: 600 }}>{includedCount} included</span>
          {' · '}
          <span style={{ color: excludedCount > 0 ? 'var(--orange)' : 'var(--text-muted)' }}>{excludedCount} excluded</span>
          {' · '}
          <span>{measurements.length} total</span>
          {isFiltered && (
            <>
              {' · '}
              <span style={{ color: 'var(--primary-blue)', fontWeight: 600 }}>
                {displayedMeasurements.length} shown
              </span>
            </>
          )}
        </span>
        {activeCount > 0 && (
          <button className="iv-btn iv-btn--sm" onClick={handleClearAll} title="Clear all filters and sort">
            ✕ Clear ({activeCount})
          </button>
        )}
        {availableExtrasKeys.size > 0 && <ColumnPicker visibleExtras={visibleExtras} onToggle={toggleExtrasCol} />}
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <BulkActionBar
          count={selectedIds.size}
          onApply={applying ? () => {} : handleBulkApply}
          onClear={() => setSelectedIds(new Set())}
        />
      )}

      <div className="iv-table-wrap">
        <table className={`iv-table${isCompact ? ' iv-table--compact' : ''}`}>
          <thead>
            <tr>
              <th style={{ width: 32 }}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => { if (el) el.indeterminate = someSelected; }}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  title="Select all visible"
                />
              </th>
              <th style={{ width: 40 }}>
                <ColHeader colKey="included" label="Inc." title="Included / Excluded" filterState={filters['included']} {...ch} />
              </th>
              <th style={{ width: 40 }}>
                <ColHeader colKey="flagged" label="Flag" title="Flagged for review" filterState={filters['flagged']} {...ch} />
              </th>
              <th>
                <ColHeader colKey="label" label="Marker 1" title="Marker 1 — user-defined ID" filterState={filters['label']} {...ch} />
              </th>
              <th>
                <ColHeader colKey="marker2" label="Marker 2" title="Marker 2" filterState={filters['marker2']} {...ch} />
              </th>
              <th>
                <ColHeader colKey="marker3" label="Marker 3" title="Marker 3" filterState={filters['marker3']} {...ch} />
              </th>
              <th style={{ minWidth: 110 }}>
                <ColHeader colKey="result" label="Result" title="Test result" filterState={filters['result']} {...ch} />
              </th>
              <th><ColHeader colKey="voltage_oc"  label="Voc"  filterState={filters['voltage_oc']}  {...ch} /></th>
              <th><ColHeader colKey="current_sc"  label="Isc"  filterState={filters['current_sc']}  {...ch} /></th>
              <th><ColHeader colKey="voltage_mpp" label="Vmpp" filterState={filters['voltage_mpp']} {...ch} /></th>
              <th><ColHeader colKey="current_mpp" label="Impp" filterState={filters['current_mpp']} {...ch} /></th>
              <th><ColHeader colKey="power_mpp"   label="Pmpp" filterState={filters['power_mpp']}   {...ch} /></th>
              <th><ColHeader colKey="fill_factor" label="FF"   filterState={filters['fill_factor']} {...ch} /></th>
              <th><ColHeader colKey="irradiance"  label="Irr." filterState={filters['irradiance']}  {...ch} /></th>
              <th><ColHeader colKey="module_temp" label="Temp" filterState={filters['module_temp']} {...ch} /></th>
              {activeExtrasCols.map((c) => (
                <th key={c.key}>
                  <ColHeader colKey={c.key} label={c.label} filterState={filters[c.key]} {...ch} />
                </th>
              ))}
              <th>
                <ColHeader colKey="notes" label="Notes" filterState={filters['notes']} {...ch} />
              </th>
              <th style={{ width: 60 }}></th>
            </tr>
          </thead>
          <tbody>
            {displayedMeasurements.map((m, index) => (
              <MeasurementRow
                key={m.id}
                id={m.id}
                index={index}
                label={m.label}
                marker2={m.marker2 ?? null}
                marker3={m.marker3 ?? null}
                selected={selectedIds.has(m.id)}
                included={m.included === 1}
                flagged={m.flagged === 1}
                voltageOc={m.voltage_oc}
                currentSc={m.current_sc}
                voltageMpp={m.voltage_mpp}
                currentMpp={m.current_mpp}
                powerMpp={m.power_mpp}
                fillFactor={m.fill_factor}
                irradiance={m.irradiance}
                moduleTemp={m.module_temp}
                result={m.result ?? null}
                resultPct={m.result_pct ?? null}
                extras={m.extras ?? null}
                notes={m.notes}
                activeExtrasCols={activeExtrasCols}
                onSelect={handleRowSelect}
                onToggleIncluded={toggleIncluded}
                onUpdate={updateMeasurement}
                onDelete={deleteMeasurement}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Row ──────────────────────────────────────────────────────────────────────

function MeasurementRow({
  id, index, label, marker2, marker3, selected, included, flagged,
  voltageOc, currentSc, voltageMpp, currentMpp, powerMpp, fillFactor,
  irradiance, moduleTemp, result, resultPct, extras, notes,
  activeExtrasCols, onSelect, onToggleIncluded, onUpdate, onDelete,
}: {
  id: string; index: number; label: string;
  marker2: string | null; marker3: string | null;
  selected: boolean; included: boolean; flagged: boolean;
  voltageOc: number | null; currentSc: number | null;
  voltageMpp: number | null; currentMpp: number | null;
  powerMpp: number | null; fillFactor: number | null;
  irradiance: number | null; moduleTemp: number | null;
  result: string | null; resultPct: number | null;
  extras: Record<string, string | number> | null;
  notes: string;
  activeExtrasCols: ExtrasColumnDef[];
  onSelect: (id: string, index: number) => void;
  onToggleIncluded: (id: string, val: boolean) => Promise<void>;
  onUpdate: (id: string, input: UpdateMeasurementInput) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}) {
  const [localIncluded, setLocalIncluded] = useState(included);
  const [localFlagged, setLocalFlagged] = useState(flagged);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState(notes);

  // Sync local state when server data changes
  if (localIncluded !== included && !editingNotes) setLocalIncluded(included);
  if (localFlagged  !== flagged  && !editingNotes) setLocalFlagged(flagged);

  const commitNotes = useCallback(() => {
    setEditingNotes(false);
    if (notesValue !== notes) onUpdate(id, { notes: notesValue });
  }, [id, notesValue, notes, onUpdate]);

  const fmtNum = (v: number | null) => (v != null ? v.toFixed(2) : '');
  const fmtFf  = (v: number | null) => (v != null ? v.toFixed(3) : '');

  return (
    <tr
      className={[
        !localIncluded ? 'iv-table__row--excluded' : '',
        selected       ? 'iv-table__row--selected'  : '',
      ].filter(Boolean).join(' ') || undefined}
    >
      <td>
        <input
          type="checkbox"
          checked={selected}
          onChange={() => {}}
          onClick={() => onSelect(id, index)}
        />
      </td>
      <td><input type="checkbox" checked={localIncluded} onChange={() => { setLocalIncluded(v => !v); onToggleIncluded(id, !localIncluded); }} /></td>
      <td><input type="checkbox" checked={localFlagged}  onChange={() => { setLocalFlagged(v => !v);  onUpdate(id, { flagged: localFlagged ? 0 : 1 }); }} /></td>
      <td><InlineTextField value={label}     placeholder="—" bold onCommit={(v) => onUpdate(id, { label:   v || label })} /></td>
      <td><InlineTextField value={marker2 ?? ''} placeholder="—" onCommit={(v) => onUpdate(id, { marker2: v || null })} /></td>
      <td><InlineTextField value={marker3 ?? ''} placeholder="—" onCommit={(v) => onUpdate(id, { marker3: v || null })} /></td>
      <td><ResultCell result={result} resultPct={resultPct} /></td>
      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--sl-text-xs)' }}>{fmtNum(voltageOc)}</td>
      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--sl-text-xs)' }}>{fmtNum(currentSc)}</td>
      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--sl-text-xs)' }}>{fmtNum(voltageMpp)}</td>
      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--sl-text-xs)' }}>{fmtNum(currentMpp)}</td>
      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--sl-text-xs)' }}>{fmtNum(powerMpp)}</td>
      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--sl-text-xs)' }}>{fmtFf(fillFactor)}</td>
      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--sl-text-xs)' }}>{fmtNum(irradiance)}</td>
      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--sl-text-xs)' }}>{fmtNum(moduleTemp)}</td>
      {activeExtrasCols.map((c) => {
        const val = extras?.[c.key];
        return <td key={c.key} style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--sl-text-xs)', color: 'var(--text-muted)' }}>{val != null ? String(val) : ''}</td>;
      })}
      <td>
        {editingNotes ? (
          <input className="iv-inline-input" style={{ width: 120 }} value={notesValue} onChange={(e) => setNotesValue(e.target.value)} onBlur={commitNotes} onKeyDown={(e) => { if (e.key === 'Enter') commitNotes(); }} autoFocus />
        ) : (
          <span style={{ cursor: 'pointer', fontSize: 'var(--sl-text-xs)', color: notes ? 'var(--text-main)' : 'var(--text-muted)' }} onClick={() => { setNotesValue(notes); setEditingNotes(true); }}>
            {notes || '...'}
          </span>
        )}
      </td>
      <td>
        <button className="iv-btn iv-btn--danger iv-btn--sm" onClick={() => { if (confirm('Delete this measurement?')) onDelete(id); }}>Del</button>
      </td>
    </tr>
  );
}

// ─── Inline Text Field ────────────────────────────────────────────────────────

function InlineTextField({ value, placeholder, bold, onCommit }: { value: string; placeholder?: string; bold?: boolean; onCommit: (v: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');

  if (editing) {
    return (
      <input
        className="iv-inline-input"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => { setEditing(false); onCommit(text); }}
        onKeyDown={(e) => { if (e.key === 'Enter') { setEditing(false); onCommit(text); } if (e.key === 'Escape') setEditing(false); }}
        autoFocus
      />
    );
  }

  return (
    <span style={{ cursor: 'pointer', fontSize: 'var(--sl-text-xs)', fontWeight: bold ? 600 : undefined, color: value ? 'var(--text-main)' : 'var(--text-muted)', whiteSpace: 'nowrap' }} onClick={() => { setText(value); setEditing(true); }}>
      {value || placeholder || '—'}
    </span>
  );
}
